import { usePendingTransferRequestsQuery } from "@api/billingTransfer.api";
import { WorkspaceDetails } from "@common/types/workspace";
import { Card } from "@design-system/Card/Card";
import { Loader } from "@design-system/Loader/Loader";
import { useActiveWorkspaceDetails } from "@hooks/useActiveWorkspaceDetails";
import { FC } from "react";
import { FLAT_RATE_PLANS, flatRatePlanNames } from "@common/flatRateBilling";
import { getBillingOwnerOrganizationForWorkspace } from "@helpers/billingOwnership";
import { Organization } from "@common/types/organization";
import { AcceptOrgBillingTransferModal } from "@components/billing/OrgBillingTransfer/AcceptOrgBillingTransferModal";
import { DeclineOrgBillingTransferModal } from "@components/billing/OrgBillingTransfer/DeclineOrgBillingTransferModal";
import { PendingOrgBillingTransferModalButton } from "@components/billing/OrgBillingTransfer/PendingOrgBillingTransferModalButton";
import { usePendingOrgBillingTransfer } from "@components/billing/OrgBillingTransfer/usePendingOrgBillingTransfer";
import { Section } from "@design-system/Section/Section";
import { useCanRequestBillingTransfer } from "@hooks/permissions/useCanRequestBillingTransfer";
import styles from "./BillingTransferPage.module.scss";
import { BillingTransferRequests } from "./BillingTransferRequests";

export const BillingTransferPage: FC = () => {
    const activeWorkspaceDetails =
        useActiveWorkspaceDetails() as WorkspaceDetails;
    const organization = getBillingOwnerOrganizationForWorkspace(
        activeWorkspaceDetails,
    ) as Organization;
    const canRequestBillingTransfer = useCanRequestBillingTransfer();
    const pendingOrgBillingTransfer = usePendingOrgBillingTransfer();

    const { data: pendingTransferRequests, isLoading } =
        usePendingTransferRequestsQuery(activeWorkspaceDetails.workspace.id);

    if (isLoading) {
        return <Loader />;
    }

    const finalPlan =
        activeWorkspaceDetails.workspace.plan ?? FLAT_RATE_PLANS.FREE;

    return (
        <div className="settings__billing billing">
            <header className="settings__header">
                <h3>Billing</h3>
            </header>

            <Section heading="Billing Transfer">
                <Card className={styles.card}>
                    <main className={styles.planInfo}>
                        <strong>{flatRatePlanNames[finalPlan]}</strong>
                        <p>
                            Your subscription is handled by {organization.name}
                        </p>
                    </main>

                    <aside className={styles.requests}>
                        <PendingOrgBillingTransferModalButton
                            modal={DeclineOrgBillingTransferModal}
                            variant="secondary"
                        >
                            Decline
                        </PendingOrgBillingTransferModalButton>
                        <PendingOrgBillingTransferModalButton
                            modal={AcceptOrgBillingTransferModal}
                            variant="primary"
                        >
                            Set up billing
                        </PendingOrgBillingTransferModalButton>
                        {canRequestBillingTransfer &&
                            !pendingOrgBillingTransfer && (
                                <BillingTransferRequests
                                    organization={organization}
                                    workspace={activeWorkspaceDetails.workspace}
                                    pendingRequestId={
                                        pendingTransferRequests?.[0]?.id
                                    }
                                />
                            )}
                    </aside>
                </Card>
            </Section>
        </div>
    );
};
